import { LicensePlan } from '../../types';

// ---- Local types for this data file ----
export interface FeatureFlag {
  included: boolean;
  /** Optional extra notes to show in UI/tooltips. */
  additionalInfo?: string;
}

export interface PlanPricing {
  E3: string;
  E5: string;
  /** Optional extra notes to show in UI/tooltips. */
  additionalInfo?: string;
}

const baseFeatures = {
  // Core Office Apps
  'Microsoft Word': { included: true, additionalInfo: 'Desktop and web versions' } as FeatureFlag,
  'Microsoft Excel': { included: true, additionalInfo: 'Desktop and web versions' } as FeatureFlag,
  'Microsoft PowerPoint': { included: true, additionalInfo: 'Desktop and web versions' } as FeatureFlag,
  'Microsoft Outlook': { included: true, additionalInfo: 'Desktop and web versions' } as FeatureFlag,
  'Microsoft Access': { included: true, additionalInfo: 'PC only' } as FeatureFlag,
  'Microsoft Publisher': { included: false, additionalInfo: 'Retired October 2024 — legacy installs only' } as FeatureFlag,
  
  // Core Services
  'Exchange Online': { E3: '100 GB mailbox', E5: 'Unlimited archive' } as PlanPricing,
  'SharePoint Online': { included: true, additionalInfo: 'Intranet and team sites' } as FeatureFlag,
  'OneDrive for Business': { included: true, additionalInfo: '1 TB cloud storage' } as FeatureFlag,
  'Microsoft Teams': { E3: false, E5: false, additionalInfo: 'Sold separately — see with-Teams suite pricing' } as { E3: boolean; E5: boolean; additionalInfo?: string },
  
  // Business Apps
  'Power BI': { E3: false, E5: 'Premium ($24/user/month)', additionalInfo: undefined } as { E3: boolean | string; E5: boolean | string; additionalInfo?: string },
  'Power Apps': { E3: 'Limited use rights', E5: 'Full use rights' } as { E3: string; E5: string; additionalInfo?: string },
  'Power Automate': { E3: 'Limited use rights', E5: 'Full use rights' } as { E3: string; E5: string; additionalInfo?: string },
  'Copilot Studio': { E3: false, E5: true, additionalInfo: 'Formerly Power Virtual Agents' } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Dataverse': { E3: false, E5: true } as { E3: boolean; E5: boolean; additionalInfo?: string },
  
  // Web Apps
  'Microsoft Forms': { included: true, additionalInfo: 'Create surveys and quizzes' } as FeatureFlag,
  'Microsoft Stream': { included: true, additionalInfo: 'Video service' } as FeatureFlag,
  'Microsoft Planner': { included: true, additionalInfo: 'Task management' } as FeatureFlag,
  'Microsoft Lists': { included: true, additionalInfo: 'Information tracking' } as FeatureFlag,
  'Microsoft Bookings': { included: true, additionalInfo: 'Scheduling' } as FeatureFlag,
  'Microsoft To Do': { included: true, additionalInfo: 'Task management' } as FeatureFlag,
  'Microsoft Sway': { included: true, additionalInfo: 'Digital storytelling' } as FeatureFlag,
  
  // Security & management (summer 2026 packaging)
  'Microsoft Defender for Office 365': { E3: 'Plan 1', E5: 'Plan 2' } as { E3: string; E5: string; additionalInfo?: string },
  'Microsoft Defender for Endpoint': { E3: 'Plan 1', E5: 'Plan 2' } as { E3: string; E5: string; additionalInfo?: string },
  'Microsoft Defender for Identity': { E3: false, E5: true } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Microsoft Defender for Cloud Apps': { E3: false, E5: true } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Microsoft Entra ID': { E3: 'P1', E5: 'P2' } as { E3: string; E5: string; additionalInfo?: string },
  'Microsoft Intune': { E3: 'Plan 1 + Plan 2', E5: 'Plan 1 + Plan 2', additionalInfo: 'Plan 2 included with summer 2026 packaging' } as { E3: string; E5: string; additionalInfo?: string },
  'Intune Remote Help': { included: true, additionalInfo: 'Included with summer 2026 packaging' } as FeatureFlag,
  'Intune Advanced Analytics': { included: true, additionalInfo: 'Included with summer 2026 packaging' } as FeatureFlag,
  'Intune Endpoint Privilege Management': { E3: false, E5: true, additionalInfo: 'E5 summer 2026 packaging' } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Intune Enterprise Application Management': { E3: false, E5: true, additionalInfo: 'E5 summer 2026 packaging' } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Microsoft Cloud PKI': { E3: false, E5: true, additionalInfo: 'E5 summer 2026 packaging' } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Microsoft Security Copilot': { E3: false, E5: true, additionalInfo: 'E5 summer 2026 packaging' } as { E3: boolean; E5: boolean; additionalInfo?: string },
  
  // Compliance
  'Data Loss Prevention': { included: true } as FeatureFlag,
  'Information Protection': { E3: 'Basic', E5: 'Advanced' } as { E3: string; E5: string; additionalInfo?: string },
  'eDiscovery': { E3: 'Basic', E5: 'Advanced + Advanced eDiscovery' } as { E3: string; E5: string; additionalInfo?: string },
  'Insider Risk Management': { E3: false, E5: true } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Records Management': { E3: false, E5: true } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Communication Compliance': { E3: false, E5: true } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Agent 365': { E3: false, E5: false, additionalInfo: 'AI agent governance — standalone $15/user/month or included in E7' } as { E3: boolean; E5: boolean; additionalInfo?: string },
  
  // Analytics & Copilot Chat
  'Viva Insights': { included: true, additionalInfo: 'Personal insights' } as FeatureFlag,
  'Advanced Analytics': { E3: false, E5: true } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Copilot Chat enhancements': { included: true, additionalInfo: 'Inbox/calendar awareness + Word, Excel, PowerPoint agents' } as FeatureFlag,
  'Copilot Chat Analytics': { included: true, additionalInfo: 'Usage and adoption analytics for Copilot Chat' } as FeatureFlag,
  
  // Voice
  'Audio Conferencing': { E3: false, E5: true } as { E3: boolean; E5: boolean; additionalInfo?: string },
  'Phone System': { E3: false, E5: true, additionalInfo: 'Calling Plan sold separately' } as { E3: boolean; E5: boolean; additionalInfo?: string },
  
  // Windows & Device Management
  'Windows 11/10 Enterprise': { included: true } as FeatureFlag,
  'Windows Autopilot': { included: true } as FeatureFlag,
  'Universal Print': { included: true } as FeatureFlag,
  'Microsoft Endpoint Manager': { included: true } as FeatureFlag
};

const createFeatures = (plan: 'E3' | 'E5') => {
  return Object.entries(baseFeatures).map(([name, value]) => {
    if ('included' in value && typeof value.included === 'boolean') {
      return {
        name,
        included: value.included,
        additionalInfo: value.additionalInfo ?? undefined
      };
    }
    if (typeof value === 'object' && 'E3' in value && 'E5' in value) {
      const planValue = value[plan];
      if (typeof planValue === 'boolean') {
        return { name, included: planValue, additionalInfo: value.additionalInfo ?? undefined };
      }
      if (typeof planValue === 'string') {
        return { name, included: true, additionalInfo: planValue };
      }
      return { name, included: false, additionalInfo: value.additionalInfo ?? undefined };
    }
    return { name, included: false };
  });
};

const e7AdditionalFeatures = [
  { name: 'Microsoft 365 Copilot', included: true, additionalInfo: 'AI-powered assistance across Microsoft 365 apps' },
  { name: 'Copilot in Word', included: true, additionalInfo: 'Draft, edit, summarize, and improve writing' },
  { name: 'Copilot in Excel', included: true, additionalInfo: 'Analyze data and create visualizations' },
  { name: 'Copilot in PowerPoint', included: true, additionalInfo: 'Create and enhance presentations' },
  { name: 'Copilot in Outlook', included: true, additionalInfo: 'Compose and summarize emails' },
  { name: 'Copilot in Teams', included: true, additionalInfo: 'Meeting summaries and real-time assistance' },
  { name: 'Agent 365', included: true, additionalInfo: 'AI agent governance, registration, and policy enforcement' },
  { name: 'Work IQ', included: true, additionalInfo: 'AI-driven workforce analytics and insights' },
  { name: 'Copilot Cowork', included: true, additionalInfo: 'Multi-step AI task orchestration' },
  { name: 'Microsoft Entra Suite', included: true, additionalInfo: 'Full identity and network access suite' },
  { name: 'Entra Private Access', included: true, additionalInfo: 'Zero Trust Network Access (ZTNA) replacing legacy VPN' },
  { name: 'Entra Internet Access', included: true, additionalInfo: 'Cloud-delivered web traffic filtering' },
  { name: 'Entra ID Governance', included: true, additionalInfo: 'Automated joiner/mover/leaver lifecycle management' },
  { name: 'Entra Verified ID', included: true, additionalInfo: 'Decentralized digital credentials' },
];

const createE7Features = (withTeams: boolean) => {
  const e5Features = createFeatures('E5').map(feature => {
    if (feature.name === 'Microsoft Entra ID') {
      return { ...feature, additionalInfo: 'P2 + Full Entra Suite' };
    }
    const override = e7AdditionalFeatures.find(f => f.name === feature.name);
    if (override) {
      if (override.name === 'Copilot in Teams') {
        return {
          ...override,
          included: withTeams,
          additionalInfo: withTeams
            ? override.additionalInfo
            : 'Requires a separate Microsoft Teams license',
        };
      }
      return override;
    }
    return feature;
  });
  const newOnly = e7AdditionalFeatures
    .filter(f => !e5Features.some(e => e.name === f.name))
    .map(feature =>
      feature.name === 'Copilot in Teams'
        ? {
            ...feature,
            included: withTeams,
            additionalInfo: withTeams
              ? feature.additionalInfo
              : 'Requires a separate Microsoft Teams license',
          }
        : feature
    );
  return [...e5Features, ...newOnly];
};

const copilotStandaloneFeatures = [
  { name: 'Microsoft 365 Copilot', included: true, additionalInfo: 'AI-powered assistance across Microsoft 365 apps' },
  { name: 'Copilot in Word', included: true, additionalInfo: 'Draft, edit, summarize, and improve writing' },
  { name: 'Copilot in Excel', included: true, additionalInfo: 'Analyze data and create visualizations' },
  { name: 'Copilot in PowerPoint', included: true, additionalInfo: 'Create and enhance presentations' },
  { name: 'Copilot in Outlook', included: true, additionalInfo: 'Compose and summarize emails' },
  { name: 'Copilot in Teams', included: true, additionalInfo: 'Meeting summaries and real-time assistance' },
];

const withTeamsFeatures = (features: ReturnType<typeof createFeatures>) =>
  features.map(feature =>
    feature.name === 'Microsoft Teams'
      ? { ...feature, included: true, additionalInfo: 'Teams included in suite' }
      : feature
  );

export const m365EnterprisePlans: LicensePlan[] = [
  {
    id: 'm365-e3',
    name: 'Microsoft 365 E3 (no Teams)',
    description: 'Core productivity apps and services with security and compliance capabilities (Teams sold separately)',
    pricing: {
      points: [
        { amount: 27.45, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 30.45, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: createFeatures('E3')
  },
  {
    id: 'm365-e3-with-teams',
    name: 'Microsoft 365 E3 with Teams',
    description: 'Microsoft 365 E3 suite with Teams included',
    pricing: {
      points: [
        { amount: 36.0, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 39.0, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: withTeamsFeatures(createFeatures('E3'))
  },
  {
    id: 'm365-e5',
    name: 'Microsoft 365 E5 (no Teams)',
    description: 'Advanced security, compliance, voice, analytics, and premium apps (Teams sold separately)',
    pricing: {
      points: [
        { amount: 48.45, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 51.45, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: createFeatures('E5')
  },
  {
    id: 'm365-e5-with-teams',
    name: 'Microsoft 365 E5 with Teams',
    description: 'Microsoft 365 E5 suite with Teams included',
    pricing: {
      points: [
        { amount: 57.0, currency: 'USD', unit: 'user/month', effective: '1970-01-01' },
        { amount: 60.0, currency: 'USD', unit: 'user/month', effective: '2026-07-01' }
      ]
    },
    features: withTeamsFeatures(createFeatures('E5'))
  },
  {
    id: 'm365-e7',
    name: 'Microsoft 365 E7 (no Teams)',
    description: 'Frontier Suite — E5 plus Copilot, Agent 365, Entra Suite, and Work IQ (Teams sold separately)',
    pricing: {
      points: [
        { amount: 90.45, currency: 'USD', unit: 'user/month', effective: '1970-01-01' }
      ]
    },
    features: createE7Features(false)
  },
  {
    id: 'm365-e7-with-teams',
    name: 'Microsoft 365 E7 with Teams',
    description: 'Microsoft 365 E7 Frontier Suite with Teams included',
    pricing: {
      points: [
        { amount: 99.0, currency: 'USD', unit: 'user/month', effective: '1970-01-01' }
      ]
    },
    features: withTeamsFeatures(createE7Features(true))
  },
  {
    id: 'm365-copilot',
    name: 'Microsoft 365 Copilot',
    description: 'Standalone Copilot add-on for eligible Microsoft 365 plans (requires qualifying base license)',
    pricing: {
      points: [
        { amount: 30.0, currency: 'USD', unit: 'user/month', effective: '1970-01-01' }
      ]
    },
    features: copilotStandaloneFeatures
  }
];
